import * as CardAccounts from '@/src/experiences/card-accounts'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_CREDIT_CARD_INFORMATION } from '@/src/common/gql/queries/card'

/**
 * Matches {@link card-platform/components/gate.tsx} logic.
 */
export const useGetCardAccount = () => {
  // Detect platform and get account data
  const getFirstAccount = CardAccounts.Hooks.useGetFirstAccount()

  // Determine platform: if data.id exists, we're on Platform, otherwise Highnote
  const isPlatform = getFirstAccount.data?.id != null

  // GraphQL query for Highnote - only skip when we KNOW it's Platform (not during loading)
  const gqlQuery = useLoggedInQuery(GET_CREDIT_CARD_INFORMATION, {
    fetchPolicy: 'cache-and-network',
    skip: isPlatform, // Only skip when confirmed Platform, NOT during initial load
  })

  // Initial loading state - wait for platform detection
  if (getFirstAccount.isLoading || getFirstAccount.isUninitialized) {
    return {
      data: undefined,
      isLoading: true,
      isUninitialized: getFirstAccount.isUninitialized,
      isError: false,
      error: undefined,
    } as const
  }

  // Platform detected with account
  if (isPlatform) {
    return {
      data: getFirstAccount.data,
      isLoading: false,
      isUninitialized: false,
      isError: false,
      error: undefined,
    } as const
  }

  // Highnote detected - wait for GraphQL to complete
  if (getFirstAccount.data) {
    // Check if GraphQL query has error
    if (gqlQuery.error) {
      return {
        data: undefined,
        isLoading: false,
        isUninitialized: false,
        isError: true,
        error: gqlQuery.error,
      } as const
    }

    // Return GraphQL data (may still be loading)
    return {
      data: gqlQuery.data?.getCreditCardInformation.node,
      isLoading: gqlQuery.loading,
      isUninitialized: false,
      isError: false,
      error: undefined,
    } as const
  }

  // Error state from platform detection
  if (getFirstAccount.error) {
    return {
      data: undefined,
      isLoading: false,
      isUninitialized: false,
      isError: true,
      error: getFirstAccount.error,
    } as const
  }

  // Fallback (should not reach here)
  return {
    data: undefined,
    isLoading: false,
    isUninitialized: false,
    isError: false,
    error: undefined,
  } as const
}
